import React from "react";
import { View, Text, StyleSheet, Platform, TouchableOpacity } from "react-native";
import FontFamily from "../../../../constants/fonts";
import { useTheme } from "../../../../theme/useTheme";

interface ToggleSwitchesProps {
  showPeriodObservation: boolean;
  setShowPeriodObservation: (value: boolean) => void;
  showWeeklyPulseCheck: boolean;
  setShowWeeklyPulseCheck: (value: boolean) => void;
  isMobile?: boolean;
}

const ToggleSwitches: React.FC<ToggleSwitchesProps> = ({
  showPeriodObservation,
  setShowPeriodObservation,
  showWeeklyPulseCheck,
  setShowWeeklyPulseCheck,
  isMobile = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark" && Platform.OS !== "web";

  // Determine which tab is active: all, period, or pulse
  const isAllActive = !showPeriodObservation && !showWeeklyPulseCheck;

  const handleAllClick = () => {
    setShowPeriodObservation(false);
    setShowWeeklyPulseCheck(false);
  };

  const handlePeriodClick = () => {
    setShowPeriodObservation(true);
    setShowWeeklyPulseCheck(false);
  };

  const handlePulseClick = () => {
    setShowPeriodObservation(false);
    setShowWeeklyPulseCheck(true);
  };

  return (
    <View
      testID="toggle-container"
      style={[
        styles.toggleContainer,
        isDark && { borderBottomColor: colors.border.default },
      ]}
    >
      <TouchableOpacity
        style={[
          styles.toggleButton,
          isAllActive && styles.toggleButtonActive,
          isDark && isAllActive && { borderBottomColor: colors.brand.primary },
        ]}
        onPress={handleAllClick}
        activeOpacity={0.7}
      >
        <Text
          style={[
            styles.toggleButtonText,
            isAllActive && styles.toggleButtonTextActive,
            isDark && { color: isAllActive ? colors.text.primary : colors.text.secondary },
          ]}
        >
          All
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[
          styles.toggleButton,
          styles.toggleButtonDefault,
          showPeriodObservation && styles.toggleButtonActive,
          isDark && { borderBottomColor: showPeriodObservation ? colors.brand.primary : colors.border.divider },
        ]}
        onPress={handlePeriodClick}
        activeOpacity={0.7}
      >
        <Text
          style={[
            styles.toggleButtonText,
            showPeriodObservation && styles.toggleButtonTextActive,
            isDark && { color: showPeriodObservation ? colors.text.primary : colors.text.secondary },
          ]}
        >
          Period Observation Needed
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[
          styles.toggleButton,
          styles.toggleButtonDefault,
          showWeeklyPulseCheck && styles.toggleButtonActive,
          isDark && { borderBottomColor: showWeeklyPulseCheck ? colors.brand.primary : colors.border.divider },
        ]}
        onPress={handlePulseClick}
        activeOpacity={0.7}
      >
        <Text
          style={[
            styles.toggleButtonText,
            showWeeklyPulseCheck && styles.toggleButtonTextActive,
            isDark && { color: showWeeklyPulseCheck ? colors.text.primary : colors.text.secondary },
          ]}
        >
          Weekly Pulse Needed
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  toggleContainer: {
    flexDirection: "row",
    gap: 0,
    paddingHorizontal: 8,
    paddingTop: 8,
    paddingBottom: 0,
    borderBottomWidth: 2,
    borderBottomColor: "#D8E7FB",
    backgroundColor: "transparent",
  },
  toggleButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  toggleButtonDefault: {
    borderBottomWidth: 1,
    borderBottomColor: "#D8E7FB",
  },
  toggleButtonActive: {
    borderBottomWidth: 1,
    borderBottomColor: "#034895",
  },
  toggleButtonText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: 20,
    color: "#4B4B4B",
  },
  toggleButtonTextActive: {
    fontWeight: "700",
    color: "#4B4B4B",
  },
});

export default ToggleSwitches;
